import { useState, useEffect, useRef, useCallback, useMemo, type ComponentProps } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import mermaid from 'mermaid'
import { Search, BookOpen, ChevronRight, Menu, X, Maximize2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { helpPages, findPage, productVersion, SECTION_LABELS, type HelpPage as HelpEntry } from '@/help/registry'
import { DIAGRAM_REGISTRY } from '@/help/diagrams/registry'
import { ARCH_TABS, archHref, isArchPage } from '@/help/architecture/tabs'
import { StructuredBlock } from '@/help/blocks/Blocks'
import { isBlockLanguage } from '@/help/blocks/parse'
import { WalkthroughPlayer } from '@/help/WalkthroughPlayer'
import { searchPages } from '@/help/search'
import { createSearchEcho } from '@/help/searchEcho'
import { SourceLine, WikiSearchResults } from '@/help/WikiSearchResults'
import { usePermission } from '@/lib/permissions'
import '@/help/help.css'

// ─── Architecture sub-tabs ───────────────────────────────────────────────────

function ArchSubTabs({ activeSlug }: { activeSlug: string }) {
  return (
    <div className='mb-6 -mt-2 border-b'>
      <div className='-mb-px flex gap-0.5 overflow-x-auto'>
        {ARCH_TABS.map(tab => (
          <Link
            key={tab.slug}
            to={archHref(tab.slug)}
            className={cn(
              'whitespace-nowrap border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
              activeSlug === tab.slug
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground',
            )}
          >
            {tab.label}
          </Link>
        ))}
      </div>
    </div>
  )
}

// ─── Mermaid block with zoom/pan fullscreen ───────────────────────────────────

function MermaidBlock({ code }: { code: string }) {
  const [id] = useState(() => `mermaid-${Math.random().toString(36).slice(2, 9)}`)
  const [svg, setSvg] = useState<string | null>(null)
  const [svgFull, setSvgFull] = useState<string | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [scale, setScale] = useState(1)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const didDrag = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const zoomRef = useRef<HTMLDivElement>(null)

  const clamp = useCallback((s: number) => Math.min(Math.max(s, 0.2), 6), [])
  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }) }, [])

  useEffect(() => {
    let cancelled = false
    mermaid
      .render(id, code)
      .then(({ svg: s }) => !cancelled && setSvg(s))
      .catch(() => !cancelled && setSvg(null))
    mermaid
      .render(`${id}-full`, code)
      .then(({ svg: s }) => !cancelled && setSvgFull(s))
      .catch(() => !cancelled && setSvgFull(null))
    return () => { cancelled = true }
  }, [id, code])

  useEffect(() => {
    if (!fullscreen) { reset(); return }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
      if (e.key === '+' || e.key === '=') setScale(s => clamp(s * 1.15))
      if (e.key === '-') setScale(s => clamp(s * 0.85))
      if (e.key === '0') reset()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [fullscreen, clamp, reset])

  useEffect(() => {
    if (!fullscreen) return
    const el = zoomRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const factor = e.deltaY > 0 ? -0.08 : 0.08
      setScale(s => clamp(s + s * factor))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [fullscreen, clamp])

  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    dragging.current = true
    didDrag.current = false
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
  }
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current) return
    didDrag.current = true
    setPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y })
  }
  const onMouseUp = () => { dragging.current = false }

  if (svg === null) {
    return (
      <pre className='my-6 overflow-x-auto rounded-lg border bg-muted/40 p-4 text-xs text-muted-foreground'>
        {code}
      </pre>
    )
  }

  const ctrl = 'flex h-8 w-8 items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white'

  return (
    <>
      <div className='group relative my-6 overflow-hidden rounded-xl border bg-card shadow-sm'>
        <button
          type='button'
          onClick={() => setFullscreen(true)}
          disabled={!svgFull}
          title='View fullscreen — zoom with scroll, pan with drag'
          aria-label='View diagram fullscreen'
          className='absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-md border bg-background/90 text-muted-foreground opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 hover:text-foreground focus-visible:opacity-100 disabled:cursor-not-allowed'
        >
          <Maximize2 className='h-3.5 w-3.5' />
        </button>
        <div
          className='flex justify-center overflow-x-auto p-6 [&_svg]:mx-auto'
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>

      {fullscreen && svgFull && (
        <div
          role='dialog'
          aria-modal='true'
          aria-label='Diagram, fullscreen'
          className='fixed inset-0 z-50 bg-black/92 backdrop-blur-md'
        >
          <div className='pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between border-b border-white/10 px-5 py-2.5'>
            <span className='text-sm font-medium text-white/70'>Diagram</span>
            <div className='pointer-events-auto flex items-center gap-1'>
              <button onClick={() => setScale(s => clamp(s * 1.2))} className={ctrl} title='Zoom in (+)'>
                <ZoomIn className='h-4 w-4' />
              </button>
              <span className='min-w-[3.5rem] text-center font-mono text-xs text-white/50'>
                {Math.round(scale * 100)}%
              </span>
              <button onClick={() => setScale(s => clamp(s * 0.8))} className={ctrl} title='Zoom out (-)'>
                <ZoomOut className='h-4 w-4' />
              </button>
              <button onClick={reset} className={ctrl} title='Reset view (0)'>
                <RotateCcw className='h-3.5 w-3.5' />
              </button>
              <div className='mx-2 h-5 w-px bg-white/15' />
              <button onClick={() => setFullscreen(false)} className={ctrl} title='Close (Esc)'>
                <X className='h-4 w-4' />
              </button>
            </div>
          </div>

          <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 border-t border-white/10 px-5 py-1.5 text-center text-[10px] text-white/30'>
            Scroll to zoom · Drag to pan · + / - / 0 · Esc or click outside to close
          </div>

          <div
            ref={zoomRef}
            className='absolute inset-0'
            style={{ cursor: dragging.current ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onClick={() => { if (!didDrag.current) setFullscreen(false) }}
          >
            <div
              className='flex h-full w-full items-center justify-center'
              style={{
                transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: dragging.current ? 'none' : 'transform 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              <div
                onClick={e => e.stopPropagation()}
                className='rounded-2xl bg-white p-5 shadow-2xl'
                style={{ height: 'calc(100vh - 40px)' }}
              >
                <div
                  className='flex h-full items-center justify-center [&_svg]:!h-full [&_svg]:!w-auto [&_svg]:!max-w-[90vw]'
                  dangerouslySetInnerHTML={{ __html: svgFull }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Custom markdown components ───────────────────────────────────────────────

function mdCode({ className, children, ...rest }: ComponentProps<'code'>) {
  const lang = /language-(\w+)/.exec(className ?? '')?.[1]
  const codeStr = String(children).replace(/\n$/, '')
  if (lang === 'mermaid') return <MermaidBlock code={codeStr} />
  if (lang === 'walkthrough') return <WalkthroughPlayer name={codeStr.trim()} />
  if (isBlockLanguage(lang)) return <StructuredBlock lang={lang} source={codeStr} />
  if (lang === 'diagram') {
    const name = codeStr.trim()
    const Diagram = DIAGRAM_REGISTRY[name]
    if (!Diagram) {
      return (
        <span className='my-6 block overflow-x-auto rounded-lg border bg-muted/40 p-4 text-xs text-muted-foreground'>
          Unknown diagram: {name}
        </span>
      )
    }
    const title = name.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')
    return <Diagram title={title} />
  }
  if (!className) {
    return (
      <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-[0.8125rem]' {...rest}>
        {children}
      </code>
    )
  }
  return (
    <code className={className} {...rest}>
      {children}
    </code>
  )
}

function mdA({ href, children }: ComponentProps<'a'>) {
  if (href?.startsWith('/')) return <Link to={href}>{children}</Link>
  return (
    <a href={href} target='_blank' rel='noopener noreferrer'>
      {children}
    </a>
  )
}

/** Fences that render as a component, not code. */
const UNBOXED_FENCES = /language-(walkthrough|diagram|cards|steps|callout|legend|guide|setup)\b/

/**
 * A walkthrough, diagram or structured block is fenced like a code sample but
 * is not code: drop the grey `<pre>` box it would otherwise sit in.
 */
function mdPre({ children, ...rest }: ComponentProps<'pre'>) {
  const child = Array.isArray(children) ? children[0] : children
  const cls = (child as { props?: { className?: string } } | null)?.props?.className ?? ''
  if (UNBOXED_FENCES.test(cls)) return <>{children}</>
  return <pre {...rest}>{children}</pre>
}

const MD_COMPONENTS = { code: mdCode, a: mdA, pre: mdPre }

// ─── TOC groups ──────────────────────────────────────────────────────────────

interface TocGroup {
  section: string
  label: string
  pages: HelpEntry[]
}

function buildGroups(): TocGroup[] {
  const map = new Map<string, HelpEntry[]>()
  for (const p of helpPages) {
    if (!map.has(p.section)) map.set(p.section, [])
    map.get(p.section)!.push(p)
  }
  // Releases first: it sits directly under the version strip in the sidebar.
  const ORDER = ['releases', '', 'user-manual', 'dev-docs']
  return ORDER.filter(s => map.has(s))
    .map(section => ({
      section,
      label: SECTION_LABELS[section] ?? section,
      pages: (map.get(section) ?? []).filter(p => p.slug !== 'index'),
    }))
    // A section with only an index page (Releases before its first note) has no heading to show.
    .filter(group => group.pages.length > 0)
}

const GROUPS = buildGroups()

// ─── Page component ──────────────────────────────────────────────────────────

export function HelpPage() {
  const { section, slug } = useParams<{ section?: string; slug?: string }>()
  const navigate = useNavigate()
  const { can } = usePermission()
  // Search lives in the URL (`?q=…&in=<section>`), so a results list can be
  // shared and Back returns to it. Page routes are unchanged (z8nrz7dyd3).
  const [searchParams, setSearchParams] = useSearchParams()
  const query = searchParams.get('q') ?? ''
  const searchSection = searchParams.get('in') ?? undefined
  const setSearch = useCallback(
    (q: string, inSection: string | undefined) => {
      const next = new URLSearchParams()
      if (q) next.set('q', q)
      if (q && inSection !== undefined) next.set('in', inSection)
      setSearchParams(next, { replace: true })
    },
    [setSearchParams],
  )
  // The box owns what is typed; the URL follows it once typing pauses. Bound
  // straight to `?q=`, the box dropped letters (see searchEcho), and searching
  // on every keystroke also re-rendered the whole page per key.
  const [typed, setTyped] = useState(query)
  const echo = useRef(createSearchEcho()).current
  const writeSearch = useCallback(
    (q: string, inSection: string | undefined) => {
      echo.wrote(q)
      setSearch(q, inSection)
    },
    [echo, setSearch],
  )
  useEffect(() => {
    // Back, a link or a shared URL changed the search: show it in the box.
    if (!echo.isEcho(query)) setTyped(query)
  }, [echo, query])
  useEffect(() => {
    if (typed === query) return
    const id = setTimeout(() => writeSearch(typed, searchSection), 250)
    return () => clearTimeout(id)
  }, [typed, query, searchSection, writeSearch])
  const [drawerOpen, setDrawerOpen] = useState(false)
  // Releases sits at the top but grows without bound, so it stays collapsed
  // until the reader asks for it, or lands on a release page.
  const [releasesOpen, setReleasesOpen] = useState(section === 'releases')

  const urlPath = section && slug ? `${section}/${slug}` : section ? `${section}/index` : 'index'
  const page = findPage(urlPath) ?? findPage('index')
  const currentSlug = slug ?? (section ? 'index' : '')

  useEffect(() => {
    mermaid.initialize({ startOnLoad: false, theme: 'neutral' })
  }, [])

  useEffect(() => {
    setDrawerOpen(false)
  }, [urlPath])

  useEffect(() => {
    if (section === 'releases') setReleasesOpen(true)
  }, [section])

  // A page gated by `permission:` is out of the reader's reach everywhere — the
  // sidebar, the page itself and, before any matching, search (AC8).
  const canAccess = useCallback((p: HelpEntry) => !p.permission || can(p.permission), [can])
  const searching = query.trim() !== ''
  const outcome = useMemo(
    () => (searching ? searchPages(helpPages, query, { canAccess, section: searchSection }) : null),
    [searching, query, canAccess, searchSection],
  )
  const pageAllowed = !page || canAccess(page)

  function NavContent() {
    return (
      <>
        <Link
          to='/wiki'
          className={cn(
            'block rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            urlPath === 'index'
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          Overview
        </Link>

        {GROUPS.map(({ section: sec, label, pages }) => {
            const collapsible = sec === 'releases'
            const visible = pages.filter(p => !p.slug.startsWith('architecture-') && canAccess(p))
            return (
              <div key={sec} className={cn(sec === GROUPS[0].section ? 'mt-3' : 'mt-5')}>
                {collapsible ? (
                  <button
                    type='button'
                    onClick={() => setReleasesOpen(open => !open)}
                    aria-expanded={releasesOpen}
                    className='mb-1 flex w-full items-center gap-1 rounded-md px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                  >
                    <ChevronRight
                      className={cn('h-3 w-3 shrink-0 transition-transform', releasesOpen && 'rotate-90')}
                    />
                    <span className='flex-1 text-left'>{label}</span>
                    <span className='font-normal tabular-nums opacity-60'>{visible.length}</span>
                  </button>
                ) : (
                  <p className='mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
                    {label}
                  </p>
                )}
                {(!collapsible || releasesOpen) &&
                  visible.map(p => (
                    <Link
                      key={p.urlPath}
                      to={`/wiki/${p.urlPath}`}
                      className={cn(
                        'mt-0.5 flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                        p.urlPath === urlPath || (isArchPage(currentSlug) && p.slug === 'architecture')
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      <ChevronRight className='h-3 w-3 shrink-0 opacity-50' />
                      {p.title}
                    </Link>
                  ))}
              </div>
            )
          })}
      </>
    )
  }

  return (
    <div className='flex h-full'>
      {drawerOpen && (
        <div
          className='fixed inset-0 z-20 bg-black/40 lg:hidden'
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r bg-background transition-transform duration-200',
          'lg:static lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className='flex h-12 shrink-0 items-center gap-2 border-b px-4'>
          <BookOpen className='h-4 w-4 text-primary' />
          <span className='text-sm font-semibold'>Wiki</span>
          <button
            onClick={() => setDrawerOpen(false)}
            className='ml-auto rounded p-0.5 text-muted-foreground hover:bg-muted lg:hidden'
          >
            <X className='h-4 w-4' />
          </button>
        </div>

        {productVersion && (
          <div className='flex shrink-0 items-center justify-between gap-2 border-b px-4 py-1.5'>
            <span className='text-[11px] text-muted-foreground'>Current version</span>
            <Link
              to={`/wiki/${productVersion.urlPath}`}
              title='Release notes'
              className='font-mono text-[11px] font-medium text-foreground transition-colors hover:text-primary'
            >
              v{productVersion.version}
            </Link>
          </div>
        )}

        <div className='shrink-0 border-b px-3 py-2'>
          <div className='relative'>
            <Search className='absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground' />
            <input
              type='search'
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setTyped('')
                  writeSearch('', undefined)
                }
              }}
              placeholder='Search the wiki…'
              aria-label='Search the wiki'
              className='h-7 w-full rounded-md border bg-muted/40 pl-7 pr-2 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring'
            />
          </div>
        </div>

        <nav className='flex-1 overflow-y-auto px-2 py-3'>
          <NavContent />
        </nav>
      </aside>

      <div className='flex min-w-0 flex-1 flex-col overflow-y-auto'>
        <div className='flex h-10 shrink-0 items-center gap-2 border-b px-4 lg:hidden'>
          <button
            onClick={() => setDrawerOpen(true)}
            className='rounded p-1 text-muted-foreground hover:bg-muted'
          >
            <Menu className='h-4 w-4' />
          </button>
          <span className='truncate text-sm text-muted-foreground'>{page?.title ?? 'Wiki'}</span>
        </div>

        {outcome ? (
          <WikiSearchResults
            query={query}
            outcome={outcome}
            section={searchSection}
            onSection={s => writeSearch(typed, s)}
          />
        ) : page && !pageAllowed ? (
          <div className='flex flex-1 items-center justify-center gap-1 text-sm text-muted-foreground'>
            You don&apos;t have access to this page.
            <button onClick={() => navigate('/wiki')} className='underline'>
              Return to Wiki
            </button>
          </div>
        ) : page ? (
          <div className='mx-auto w-full max-w-3xl px-8 py-10'>
            {section === 'dev-docs' && isArchPage(currentSlug) && (
              <ArchSubTabs activeSlug={currentSlug} />
            )}
            {page.section === 'releases' && page.slug === 'index' && productVersion && (
              <div className='mb-8 rounded-lg border bg-muted/30 p-4'>
                <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Current version
                </p>
                <Link
                  to={`/wiki/${productVersion.urlPath}`}
                  className='mt-1 inline-block font-mono text-2xl font-semibold text-foreground hover:underline'
                >
                  v{productVersion.version}
                </Link>
              </div>
            )}

            <div className='help-content'>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={MD_COMPONENTS}
              >
                {page.content}
              </ReactMarkdown>
            </div>

            <SourceLine page={page} className='mt-16 border-t pt-4' />
            <p className='mt-2 text-xs text-muted-foreground'>
              {page.section === 'releases' && page.slug !== 'index' ? (
                'This page is generated automatically on each release.'
              ) : (
                <>
                  Edit this page:{' '}
                  <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-[0.75rem]'>
                    src/help/content/{page.urlPath}.md
                  </code>
                </>
              )}
            </p>
          </div>
        ) : (
          <div className='flex flex-1 items-center justify-center gap-1 text-sm text-muted-foreground'>
            Page not found.
            <button onClick={() => navigate('/wiki')} className='underline'>
              Return to Wiki
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
